<template>
    <div class="carousel-container">
        <!-- Left Scroll Button -->
        <button
            @click="scrollLeft"
            class="carousel-btn left-btn"
            :style="{ display: activeIndex === 0 ? 'none' : 'block' }">
            <i class="mdi mdi-chevron-left"></i>
        </button>

        <!-- Gallery container where PhotoSwipe will be initialized -->
        <div class="carousel-wrapper">
            <div id="my-gallery" class="carousel-track" ref="carouselTrack">
                <a
                    v-for="(mediaItem, index) in mediaListInternal"
                    :key="index + ':' + mediaItem.media"
                    :href="mediaItem.media"
                    :data-pswp-width="mediaItem.width"
                    :data-pswp-height="mediaItem.height"
                    target="_blank"
                    class="carousel-item"
                    :class="{ 'carousel-item--pending': !isOpenable(mediaItem) }"
                    :data-pswp-type="mediaItem.type"
                    :style="getItemStyle(index)"
                >
                    <!-- an image shows itself, a video its poster frame; hidden until loaded so neither
                         the alt text nor a previous image shows through the placeholder. The same file
                         fills the tile behind the frame as a blurred backdrop. -->
                    <img
                        class="carousel-item__backdrop"
                        :src="tileSrc(mediaItem)"
                        alt=""
                        aria-hidden="true"
                        :style="{ visibility: mediaItem.loaded ? 'visible' : 'hidden' }"
                    />
                    <!-- the frame carries the card color so transparent media show it, not the blur -->
                    <div class="carousel-item__frame" :style="{ backgroundColor: theme.backgroundColor }">
                        <img
                            :src="tileSrc(mediaItem)"
                            :alt="'Image ' + index"
                            :style="{ visibility: mediaItem.loaded ? 'visible' : 'hidden' }"
                            @load="onTileLoaded(mediaItem, $event.target)"
                            @error="onTileError(mediaItem)"
                        />
                    </div>
                    <!-- placeholder with spinner until the tile is there, like the native media view -->
                    <div v-if="!mediaItem.loaded" class="carousel-placeholder" :style="placeholderStyle">
                        <i
                            v-if="mediaItem.error"
                            class="mdi"
                            :class="mediaItem.type === 'video' ? 'mdi-play-circle-outline' : 'mdi-image-broken-variant'"
                            :style="{ color: theme.btnColorPrimary }"
                        ></i>
                        <v-progress-circular v-else indeterminate :size="40" :color="theme.btnColorPrimary" />
                    </div>
                </a>
            </div>
        </div>

        <!-- Right Scroll Button -->
        <button
            @click="scrollRight"
            class="carousel-btn right-btn"
            :style="{ display: activeIndex >= mediaList.length - 1 ? 'none' : 'block' }">
            <i class="mdi mdi-chevron-right"></i>
        </button>
    </div>
</template>

<script>
// Import PhotoSwipeLightbox and style
import {layoutMixin} from "../../utils/layout-mixin";
import { withAlpha } from '../../utils/color.mjs';
import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js';
import PhotoSwipeVideoPlugin from 'photoswipe-video-plugin/dist/photoswipe-video-plugin.esm.js';
import 'photoswipe/dist/photoswipe.css';

export default {
    mixins: [layoutMixin],
    props: {
        mediaList: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            activeIndex: 0, // index of the tile currently shown
            mediaListInternal:[],
        };
    },
    watch: {
        mediaList() {
            // Reset the active index when the media list changes
            this.activeIndex = 0;
            this.loadMediaList();
        },
    },
    computed: {
        // event text color as a translucent value; the CSS fallback stays without a theme
        placeholderStyle() {
            const color = withAlpha(this.theme.textColor, 0.15);
            return color ? { backgroundColor: color } : null;
        },
        // one tile at a time, the others stay in the DOM so their images keep loading
        getItemStyle() {
            return (index) => ({ display: index === this.activeIndex ? 'block' : 'none' });
        },
    },
    mounted() {
        // Initialize PhotoSwipeLightbox after the gallery is mounted
        const lightbox = new PhotoSwipeLightbox({
            gallery: '#my-gallery', // Target gallery element
            children: 'a', // Target each <a> tag inside the gallery

            pswpModule: () => import('photoswipe'), // Lazy load the PhotoSwipe module
        });

        new PhotoSwipeVideoPlugin(lightbox);

        const fullscreenBg = (this.theme && this.theme.imageFullscreenBackground)
            ? this.theme.imageFullscreenBackground
            : '#000';

        const applyPswpItemBackground = () => {
            const items = document.querySelectorAll('.pswp__item');
            items.forEach((el) => {
                el.style.background = fullscreenBg;
            });
        };

        lightbox.on('open', applyPswpItemBackground);
        lightbox.on('contentActivate', applyPswpItemBackground);
        lightbox.on('change', applyPswpItemBackground);
        // the video plugin autoplays on activate; fires for images too, hence the type check
        lightbox.on('contentActivate', ({ content }) => {
            if (content && content.data && content.data.type === 'video') {
                this.$emit('videoActivate');
            }
        });

        lightbox.init();
        // plain instance property, not data(): the lightbox must not be made reactive
        this.lightbox = lightbox;
        //load also the media
        this.loadMediaList();
    },
    // The gallery is destroyed whenever the spot details are closed or switched; without destroy()
    // the click listener on the gallery element and the plugin listeners would leak per mount.
    beforeDestroy() {
        if (this.lightbox) {
            this.lightbox.destroy();
            this.lightbox = null;
        }
    },
    updated() {

    },
    methods: {
        /**
         * Rebuilds the tile list synchronously so every tile renders at once behind a placeholder.
         * The lightbox size arrives per item afterwards (image: from the tile itself, video: from a
         * metadata probe); media already in the list keep their state instead of reloading.
         */
        loadMediaList() {
            const previous = new Map(this.mediaListInternal.map((item) => [item.media, item]));

            this.mediaListInternal = this.mediaList.map((media) => {
                if (previous.has(media)) {
                    return previous.get(media);
                }
                const type = this.isImage(media) ? 'image' : 'video';
                const item = {
                    type: type,
                    media: media,
                    mediaThumb: type === 'video' ? this.getVideoThumb(media) : null,
                    width: 0,
                    height: 0,
                    loaded: false,
                    error: false,
                };
                if (type === 'video') {
                    this.probeVideoSize(item);
                }
                return item;
            });
        },

        // The tile is the browser's only download of an image, so its natural size doubles as the
        // lightbox size. A video poster only fills in until the metadata probe delivers the real size.
        onTileLoaded(item, img) {
            item.loaded = true;
            if (item.type === 'image' || item.width === 0) {
                this.setFittedSize(item, img.naturalWidth || 1200, img.naturalHeight || 800);
            }
        },

        onTileError(item) {
            item.error = true;
        },

        // Only metadata is requested; a failed probe leaves the poster size in place.
        probeVideoSize(item) {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.addEventListener('loadedmetadata', () => {
                if (video.videoWidth && video.videoHeight) {
                    this.setFittedSize(item, video.videoWidth, video.videoHeight);
                }
            });
            video.addEventListener('error', () => {
                console.error(`Failed to load video dimensions for ${item.media}`);
            });
            video.src = item.media;
            video.load();
        },

        // Scales to the lightbox viewport (full width on phones, 90 % otherwise) keeping the aspect ratio.
        setFittedSize(item, width, height) {
            const maxWidth = window.innerWidth <= 768 ? window.innerWidth : window.innerWidth * 0.9;
            const maxHeight = window.innerHeight * 0.9;
            const scale = Math.min(maxWidth / width, maxHeight / height);
            item.width = width * scale;
            item.height = height * scale;
        },

        // PhotoSwipe needs the size to open a slide; pending tiles ignore clicks. A video opens as soon
        // as its size is known, even when its poster is missing.
        isOpenable(item) {
            return item.width > 0 && (item.loaded || item.type === 'video');
        },

        /**
         * Generates a thumbnail URL from a video URL by replacing the file extension
         * @param {string} media - The video URL
         * @returns {string} The thumbnail URL
         */
        getVideoThumb(media) {
            // Extract the video URL and generate the thumbnail URL
            const videoUrl = media;
            const thumbUrl = videoUrl.replace(/\.mp4$/, '_thumb.png');
            return thumbUrl;
        },
        scrollLeft() {
            // Move the active index to the left if possible
            if (this.activeIndex > 0) {
                this.activeIndex -= 1;
            }
        },
        scrollRight() {
            // Move the active index to the right if possible
            if (this.activeIndex < this.mediaList.length - 1) {
                this.activeIndex += 1;
            }
        },
        isImage(media) {
            // Check if the media URL is an image based on its extension
            return /\.(jpg|jpeg|png|gif)$/i.test(media);
        },
        tileSrc(item) {
            return item.type === 'image' ? item.media : item.mediaThumb;
        },
    },
};
</script>
<style lang="scss">
//override the bg opacity
.pswp__bg {
    opacity: 1 !important;
}
</style>
<style scoped lang="scss">
.carousel-container {
    position: relative;
    width: 100%;
    overflow: hidden;
    max-width: 1200px;
    margin: 0 auto;

    .carousel-wrapper {
        overflow: hidden;
        width: 100%;
    }

    .carousel-track {
        display: flex;
        align-items: flex-start;
        transition: transform 0.3s ease;
        scroll-behavior: smooth;
    }

    // Tile height: 16:9 of the width, capped at 40 % of the card's content area (the size container
    // in HLCardLeftLayout). The vh value is the fallback where container units are unknown.
    .carousel-item {
        position: relative;
        flex: 0 0 100%;
        aspect-ratio: 16 / 9;
        max-height: 40vh;
        max-height: 40cqh;
        overflow: hidden;

        // the medium blurred over the whole tile, scaled so the blur's faded edge stays outside
        &__backdrop {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            filter: blur(24px) brightness(0.75);
            transform: scale(1.15);
        }

        // 16:9 frame over the full tile height, centered; narrower than the tile once the height cap applies
        &__frame {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 50%;
            aspect-ratio: 16 / 9;
            max-width: 100%;
            transform: translateX(-50%);
            overflow: hidden;
            background: #fff;
            box-shadow: 0 0 30px rgba(0, 0, 0, 0.35);

            img {
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        &--pending {
            pointer-events: none;
        }
    }

    .carousel-placeholder {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #e0e0e0;
        color: #757575;
        font-size: 3rem;
    }

    .carousel-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background-color: rgba(0, 0, 0, 0.5);
        border: none;
        color: white;
        font-size: 2rem;
        cursor: pointer;
        z-index: 1;
        border-radius: 50%;
        width: 40px;
        height: 40px;

        &.left-btn {
            left: 15px;
        }

        &.right-btn {
            right: 15px;
        }
    }
}
</style>
